import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import StatsCards from '../components/Dashboard/StatsCards';
import ChainStatus from '../components/Dashboard/ChainStatus';
import RecentTransactions from '../components/Dashboard/RecentTransactions';
import AlertsOverview from '../components/Dashboard/AlertsOverview';
import ValidatorsStatus from '../components/Dashboard/ValidatorsStatus';
import VolumeChart from '../components/Dashboard/VolumeChart';

const Dashboard: React.FC = () => {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: () => apiClient.getDashboardStats(),
    refetchInterval: autoRefresh ? 30000 : false, // Only refresh if autoRefresh is enabled
  });

  const { data: chainStatus, isLoading: chainLoading } = useQuery({
    queryKey: ['chainStatus'],
    queryFn: () => apiClient.getChainStatus(),
    refetchInterval: autoRefresh ? 10000 : false, // Only refresh if autoRefresh is enabled
  });

  const { data: recentTransactions } = useQuery({
    queryKey: ['recentTransactions'],
    queryFn: () => apiClient.getTransactions({}, 1, 5),
    refetchInterval: autoRefresh ? 15000 : false,
  });

  const { data: recentAlerts } = useQuery({
    queryKey: ['recentAlerts'],
    queryFn: () => apiClient.getAlerts({}, 1, 5),
    refetchInterval: autoRefresh ? 15000 : false,
  });

  const { data: validatorHealth } = useQuery({
    queryKey: ['validatorHealth'],
    queryFn: () => apiClient.getValidatorHealthSummary(),
    refetchInterval: autoRefresh ? 15000 : false, // More frequent updates for health data
  });

  if (statsLoading || chainLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bridge Audit Dashboard</h1>
            <p className="text-gray-600 mt-1">Real-time monitoring of PFVS bridge operations</p>
          </div>
          <div className="flex items-center space-x-3">
            <label className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Auto-refresh</span>
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  autoRefresh ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoRefresh ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </label>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards stats={dashboardStats} />

      {/* Chain Status */}
      <ChainStatus chains={chainStatus} />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Volume Chart */}
        <div className="lg:col-span-2">
          <VolumeChart />
        </div>

        {/* Recent Transactions */}
        <RecentTransactions transactions={recentTransactions?.items || []} />

        {/* Alerts Overview */}
        <AlertsOverview alerts={recentAlerts?.items || []} />
      </div>

      {/* Validators Status */}
      <ValidatorsStatus healthSummary={validatorHealth || null} />
    </div>
  );
};

export default Dashboard; 